import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  RadioGroup,
  RadioGroupItem,
} from '../../components/ui/radio-group';
import { useToast } from '../../hooks/use-toast';
import { recordConsent } from '../../components/CookieConsentBanner';
import { Checkbox } from '../../components/ui/checkbox';
import {
  RadioTower,
  Antenna,
  Building2,
  Wrench,
  Compass,
  ShieldCheck,
  ClipboardList,
  PenTool,
  HardHat,
  Activity,
  CheckCircle2,
  LifeBuoy,
  Wifi,
  Network,
  Cpu,
  Landmark,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  Building,
  User,
  Upload,
  Zap,
  Ruler,
  Signal,
} from 'lucide-react';

const TelecomTowerRF = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [form, setForm] = useState({
    companyName: '',
    contactPerson: '',
    mobile: '',
    email: '',
    siteLocation: '',
    towerType: '',
    height: '',
    rfEquipment: '',
    powerAvailable: '',
    notes: '',
  });
  const [files, setFiles] = useState([]);

  const services = [
    {
      icon: RadioTower,
      title: 'GW Tower Installation',
      description:
        'Ground-based Guyed and Self-Supporting Wire (GW) tower deployment with structural design, foundation works and end-to-end installation.',
    },
    {
      icon: Building2,
      title: 'SS Tower Installation',
      description:
        'Self-Supporting (SS) lattice towers built for high-load RF deployments — survey, fabrication, erection and commissioning.',
    },
    {
      icon: Antenna,
      title: 'Rooftop RF Deployment',
      description:
        'Rooftop pole, mast and platform installations for ISPs and enterprises with weatherproof cabling and safety compliance.',
    },
    {
      icon: Signal,
      title: 'RF Antenna Mounting',
      description:
        'Precise mounting and orientation of point-to-point, sectoral and microwave RF antennas with vendor-agnostic hardware.',
    },
    {
      icon: Compass,
      title: 'Alignment & LOS Survey',
      description:
        'Line-of-Sight surveys, azimuth and tilt alignment, link budgeting and field validation for guaranteed throughput.',
    },
    {
      icon: Wrench,
      title: 'AMC & Maintenance',
      description:
        'Annual maintenance contracts covering preventive checks, emergency repairs, tower painting and uptime SLAs.',
    },
  ];

  const processSteps = [
    { icon: ClipboardList, label: 'Survey' },
    { icon: PenTool, label: 'Design' },
    { icon: HardHat, label: 'Installation' },
    { icon: Activity, label: 'RF Alignment' },
    { icon: CheckCircle2, label: 'Testing' },
    { icon: LifeBuoy, label: 'Maintenance' },
  ];

  const useCases = [
    {
      icon: Wifi,
      title: 'ISPs & Broadband Providers',
      description:
        'Last-mile RF backhaul, PoP towers and rooftop deployments for fixed wireless ISPs scaling subscribers.',
    },
    {
      icon: RadioTower,
      title: 'Telecom Operators',
      description:
        'Greenfield and rollout support for SS/GW towers across metros, semi-urban and rural circles.',
    },
    {
      icon: Network,
      title: 'Smart City Projects',
      description:
        'Smart pole and rooftop RF infrastructure for surveillance, public Wi-Fi and IoT connectivity grids.',
    },
    {
      icon: Cpu,
      title: 'Enterprise Campuses',
      description:
        'Inter-building RF links, factory-to-office connectivity and DR site backhaul over licensed/unlicensed spectrum.',
    },
    {
      icon: Landmark,
      title: 'Government Networks',
      description:
        'Secure RF deployments for defense, public safety, e-governance and rural connectivity programs.',
    },
  ];

  const whySipl = [
    {
      icon: ShieldCheck,
      title: 'End-to-End Execution',
      description:
        'Single accountable partner from site survey to long-term maintenance — no handoffs, no finger-pointing.',
    },
    {
      icon: RadioTower,
      title: 'Large-Scale RF Experience',
      description:
        'Hundreds of tower and rooftop sites delivered for ISPs, operators and enterprises across India.',
    },
    {
      icon: Network,
      title: 'Multi-Vendor Compatibility',
      description:
        'Certified to deploy Cambium, Ubiquiti, Mikrotik, RADWIN, Ceragon and other leading RF platforms.',
    },
    {
      icon: Activity,
      title: 'Fast Turnaround',
      description:
        'Pre-engineered kits, in-house riggers and pan-India crew network enable quick site activation.',
    },
  ];

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(list);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!consentGiven) {
      toast({
        title: 'Consent required',
        description: 'Please accept the DPDPA consent notice to submit.',
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);

    // Record DPDPA consent (proxy via backend)
    recordConsent({
      principal_id: `rf_lead_${form.email || Date.now()}`,
      principal_email: form.email || undefined,
      principal_name: form.contactPerson || undefined,
      source_application: 'sanghvi-website',
      channel: 'rf-lead-form',
    });

    // UI-only submission — backend wiring will be added in next iteration
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      toast({
        title: 'Request received',
        description: 'Our RF team will contact you within 24 hours.',
      });
      setForm({
        companyName: '',
        contactPerson: '',
        mobile: '',
        email: '',
        siteLocation: '',
        towerType: '',
        height: '',
        rfEquipment: '',
        powerAvailable: '',
        notes: '',
      });
      setFiles([]);
      setConsentGiven(false);
    }, 700);
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Telecom Tower Installation & RF Mounting Services | Sanghvi Infotech</title>
        <meta
          name="description"
          content="End-to-end telecom tower installation, RF antenna mounting, LOS survey and AMC services for ISPs, telecom operators, smart cities and enterprises."
        />
        <meta
          name="keywords"
          content="GW Tower, SS Tower, RF Mounting, ISP Infrastructure, Telecom Tower Installation, RF Deployment, AMC, LOS Survey"
        />
      </Helmet>

      {/* SECTION 1: HERO */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&h=800&fit=crop"
            alt="Telecom Tower"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <RadioTower className="h-16 md:h-20 w-16 md:w-20 mx-auto mb-6 text-cyan-300 animate-float" />
            <h1
              data-testid="rf-hero-title"
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              Telecom Tower & RF Infrastructure Services
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 font-semibold">
              End-to-End Deployment of GW & SS Towers with Precision RF Mounting
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#rf-lead-form">
                <Button
                  data-testid="rf-cta-survey"
                  size="lg"
                  className="bg-cyan-400 text-slate-900 hover:bg-cyan-300 text-base md:text-lg px-6 md:px-8 transition-all hover:scale-105"
                >
                  Request Site Survey
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link to="/contact">
                <Button
                  data-testid="rf-cta-expert"
                  size="lg"
                  variant="outline"
                  className="border-white text-white bg-transparent hover:bg-white hover:text-slate-900 text-base md:text-lg px-6 md:px-8 transition-all hover:scale-105"
                >
                  Talk to Expert
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* SECTION 2: SERVICES GRID */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our RF & Tower Services
            </h2>
            <div className="w-24 h-1 bg-cyan-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From greenfield tower erection to last-mile RF mounting and SLA-backed maintenance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  data-testid={`rf-service-card-${index}`}
                  className="border border-gray-100 shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
                >
                  <CardHeader>
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3: PROCESS FLOW */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Deployment Process
            </h2>
            <div className="w-24 h-1 bg-cyan-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A proven six-stage workflow that takes you from concept to live, maintained RF infrastructure.
            </p>
          </div>

          <div className="hidden md:flex items-center justify-between max-w-6xl mx-auto">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={index}>
                  <div
                    data-testid={`rf-process-step-${index}`}
                    className="flex flex-col items-center text-center group"
                  >
                    <div className="w-16 h-16 rounded-full bg-white border-2 border-cyan-500 flex items-center justify-center shadow-lg group-hover:bg-cyan-500 transition-colors">
                      <Icon className="h-7 w-7 text-cyan-600 group-hover:text-white transition-colors" />
                    </div>
                    <span className="mt-3 font-semibold text-gray-800 text-sm">
                      {step.label}
                    </span>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 mx-2"></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Mobile vertical flow */}
          <div className="md:hidden space-y-4 max-w-md mx-auto">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white p-4 rounded-lg shadow-md"
                >
                  <div className="w-12 h-12 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Step {index + 1}</span>
                    <h4 className="font-semibold text-gray-900">{step.label}</h4>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4: USE CASES */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>
            <div className="w-24 h-1 bg-cyan-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Trusted by network builders across telecom, public infrastructure and enterprise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {useCases.map((uc, index) => {
              const Icon = uc.icon;
              return (
                <Card
                  key={index}
                  data-testid={`rf-usecase-${index}`}
                  className="border-l-4 border-cyan-500 shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <Icon className="h-10 w-10 text-cyan-600 mb-3" />
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{uc.title}</h3>
                    <p className="text-gray-600 text-sm">{uc.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 5: WHY SIPL */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 to-blue-950 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Sanghvi Infotech</h2>
            <div className="w-24 h-1 bg-cyan-400 mx-auto mb-6"></div>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto">
              A field-proven RF and tower deployment partner — engineered for scale, speed and uptime.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {whySipl.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  data-testid={`rf-why-sipl-${index}`}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all"
                >
                  <Icon className="h-10 w-10 text-cyan-300 mb-4" />
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-blue-100 text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6: LEAD CAPTURE FORM */}
      <section id="rf-lead-form" className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Request RF Deployment / Tower Installation
              </h2>
              <div className="w-24 h-1 bg-cyan-500 mx-auto mb-6"></div>
              <p className="text-lg text-gray-600">
                Share your site details and our RF team will revert within 24 hours.
              </p>
            </div>

            <Card className="shadow-2xl border-none overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600"></div>
              <CardContent className="p-6 md:p-10">
                {submitted ? (
                  <div
                    data-testid="rf-form-success"
                    className="text-center py-10"
                  >
                    <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Thank you!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Our RF team will contact you within 24 hours.
                    </p>
                    <Button
                      data-testid="rf-form-reset"
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                    >
                      Submit another request
                    </Button>
                  </div>
                ) : (
                  <form
                    data-testid="rf-lead-form"
                    onSubmit={handleSubmit}
                    className="space-y-6"
                  >
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="companyName" className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-cyan-600" /> Company Name *
                        </Label>
                        <Input
                          id="companyName"
                          data-testid="rf-input-company"
                          required
                          value={form.companyName}
                          onChange={handleChange('companyName')}
                          placeholder="Acme Networks Pvt. Ltd."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactPerson" className="flex items-center gap-2">
                          <User className="h-4 w-4 text-cyan-600" /> Contact Person *
                        </Label>
                        <Input
                          id="contactPerson"
                          data-testid="rf-input-contact"
                          required
                          value={form.contactPerson}
                          onChange={handleChange('contactPerson')}
                          placeholder="Full name"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="mobile" className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-cyan-600" /> Mobile Number *
                        </Label>
                        <Input
                          id="mobile"
                          data-testid="rf-input-mobile"
                          required
                          type="tel"
                          value={form.mobile}
                          onChange={handleChange('mobile')}
                          placeholder="+91 98xxxxxxxx"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-cyan-600" /> Email *
                        </Label>
                        <Input
                          id="email"
                          data-testid="rf-input-email"
                          required
                          type="email"
                          value={form.email}
                          onChange={handleChange('email')}
                          placeholder="you@company.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="siteLocation" className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-cyan-600" /> Site Location *
                      </Label>
                      <Input
                        id="siteLocation"
                        data-testid="rf-input-location"
                        required
                        value={form.siteLocation}
                        onChange={handleChange('siteLocation')}
                        placeholder="Address, city, state, pincode (or paste Google Maps link)"
                      />
                      <p className="text-xs text-gray-500">
                        Paste a Google Maps share link or full address. Map picker coming soon.
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <RadioTower className="h-4 w-4 text-cyan-600" /> Tower Type *
                        </Label>
                        <Select
                          value={form.towerType}
                          onValueChange={handleChange('towerType')}
                        >
                          <SelectTrigger data-testid="rf-select-tower-type">
                            <SelectValue placeholder="Select tower type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GW">GW (Guyed Wire)</SelectItem>
                            <SelectItem value="SS">SS (Self-Supporting)</SelectItem>
                            <SelectItem value="Rooftop">Rooftop</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="height" className="flex items-center gap-2">
                          <Ruler className="h-4 w-4 text-cyan-600" /> Required Height (meters)
                        </Label>
                        <Input
                          id="height"
                          data-testid="rf-input-height"
                          type="number"
                          min="0"
                          value={form.height}
                          onChange={handleChange('height')}
                          placeholder="e.g. 30"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="rfEquipment" className="flex items-center gap-2">
                        <Antenna className="h-4 w-4 text-cyan-600" /> RF Equipment Type
                      </Label>
                      <Input
                        id="rfEquipment"
                        data-testid="rf-input-equipment"
                        value={form.rfEquipment}
                        onChange={handleChange('rfEquipment')}
                        placeholder="e.g. Cambium PTP 550, Ubiquiti AirFiber, RADWIN 5000"
                      />
                    </div>

                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-cyan-600" /> Power Availability *
                      </Label>
                      <RadioGroup
                        value={form.powerAvailable}
                        onValueChange={handleChange('powerAvailable')}
                        className="flex gap-6"
                        required
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="Yes"
                            id="power-yes"
                            data-testid="rf-radio-power-yes"
                          />
                          <Label htmlFor="power-yes" className="cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="No"
                            id="power-no"
                            data-testid="rf-radio-power-no"
                          />
                          <Label htmlFor="power-no" className="cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="files" className="flex items-center gap-2">
                        <Upload className="h-4 w-4 text-cyan-600" /> Upload Site Images (optional)
                      </Label>
                      <Input
                        id="files"
                        data-testid="rf-input-files"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFiles}
                        className="cursor-pointer"
                      />
                      {files.length > 0 && (
                        <p className="text-xs text-gray-600">
                          {files.length} file(s) selected
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Additional Notes</Label>
                      <Textarea
                        id="notes"
                        data-testid="rf-input-notes"
                        value={form.notes}
                        onChange={handleChange('notes')}
                        placeholder="Anything else our team should know"
                        rows={4}
                      />
                    </div>

                    <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-50 border border-slate-200">
                      <Checkbox
                        id="rf-consent"
                        data-testid="rf-consent-checkbox"
                        checked={consentGiven}
                        onCheckedChange={setConsentGiven}
                        className="mt-0.5"
                      />
                      <Label
                        htmlFor="rf-consent"
                        className="text-sm text-slate-700 font-normal leading-relaxed cursor-pointer flex items-start gap-2"
                      >
                        <ShieldCheck className="h-4 w-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                        <span>
                          I consent to Sanghvi Infotech processing the personal
                          and site data submitted here for the purpose of
                          responding to my RF deployment enquiry, in line with
                          India&apos;s DPDPA.{' '}
                          <Link
                            to="/privacy-policy"
                            className="text-cyan-600 hover:text-cyan-700 underline underline-offset-2"
                          >
                            Privacy policy
                          </Link>
                          .
                        </span>
                      </Label>
                    </div>

                    <Button
                      data-testid="rf-form-submit"
                      type="submit"
                      size="lg"
                      disabled={submitting || !consentGiven}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-base md:text-lg transition-all hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                    >
                      {submitting ? 'Submitting...' : 'Submit Request'}
                      {!submitting && <ArrowRight className="ml-2 h-5 w-5" />}
                    </Button>
                    <p className="text-xs text-center text-gray-500">
                      By submitting, you agree to be contacted by our RF team regarding this enquiry.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TelecomTowerRF;
